import { styled } from "@linaria/react"
import { Link } from "@tanstack/react-router"
import { observer } from "mobx-react-lite"
import { useState } from "react"

import { Avatar } from "@/components/Avatar/Avatar.tsx"
import { Button } from "@/components/Button/Button.tsx"
import { ConfirmDialog, Dialog } from "@/components/Dialog/Dialog.tsx"
import { Ellipsis } from "@/components/Ellipsis/Ellipsis.ts"
import { Horizontal, Spring, Vertical } from "@/components/Flex.ts"
import { FullWidthInput, INPUT_HEIGHT_PX, INPUT_PADDING_PX } from "@/components/Input/Input.tsx"
import { BatchLoader } from "@/components/IntersectionObserver/BatchLoader.tsx"
import {
  SelectContent,
  SelectIcon,
  SelectItem,
  SelectItemText,
  SelectPortal,
  SelectRoot,
  SelectTrigger,
  SelectValue,
  SelectViewport
} from "@/components/Select/Select.tsx"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { useModel } from "@/hooks/useModel.ts"
import { getFullName, useMyPrincipal } from "@/hooks/useProfile.ts"
import { useOrgContext, useQueries } from "@/hooks/useRootContext.ts"
import { ChevronDownLight, DeleteLight, Icon20 } from "@/icons/Icons.ts"
import { formatErrorMessage, pluralize } from "@/lib/displayUtils.ts"
import { validateEmail } from "@/lib/validateUtils.ts"
import { InviteTeammatesButton, InviteTeammatesButtonIcon } from "@/routes/auth/InviteTeammatesButton.ts"
import { InviteTeammatesDialogModel } from "@/routes/auth/InviteTeammatesDialogModel.ts"
import { Navigator } from "@/routes/Navigator.ts"
import { ellipsis } from "@/styles/mixins.ts"
import * as textStyles from "@/styles/textStyles.ts"
import { ellipsize } from "@/styles/textStyles.ts"

import type {
  InvitationLandingContext,
  OrganizationMember,
  OrganizationMemberRole
} from "@/lib/client/generated/TypeDefinitions.ts"


export const InviteTeammatesDialog = ({ dialogOpen, setDialogOpen, landingContext }: {
  dialogOpen: boolean
  setDialogOpen: (open: boolean) => void
  landingContext?: InvitationLandingContext
}) => {
  return (
    <Dialog
      open={dialogOpen}
      onOpenChange={setDialogOpen}
      trigger={null}
      title="Invite Teammates"
    >
      <InviteTeammatesDialogContent setDialogOpen={setDialogOpen} landingContext={landingContext} />
    </Dialog>
  )
}

export const InviteTeammatesDialogContent = observer(({ setDialogOpen, landingContext }: {
  setDialogOpen: (open: boolean) => void
  landingContext?: InvitationLandingContext
}) => {
  const queries = useQueries()
  const context = useOrgContext()
  const currentUser = useMyPrincipal()

  const model = useModel(() => {
    return new InviteTeammatesDialogModel(context, queries, currentUser, landingContext)
  }, [context, queries, currentUser, landingContext])

  const invitations = model.invitations.get()
  const invitationsToSend = invitations.filter(invitation => invitation.valid && invitation.email.trim() !== "")

  return (
    <InviteTeammatesFormContainer>
      <InvitationsContainer>
        {invitations.map((invitation, idx) => (
          <PendingInvitationContainer key={idx}>
            <PendingEmailContainer>
              <FullWidthInput
                id={`email-${idx}`}
                placeholder="Email"
                data-testid={`invite-email-${idx}`}
                value={invitation.email}
                autoComplete="off"
                onKeyDown={(e) => {
                  if (e.key == "Enter" && validateEmail(e.currentTarget.value)) {
                    model.addEmailField()
                  }
                }}
                onChange={e => model.updateField(idx, { email: e.target.value })}
                onBlur={(e) => model.markTouched(idx, e.target.value.trim() !== "")}
              />

              {invitation.touched && !invitation.valid && (
                <InputErrorMessage data-testid="invite-error">The email address is not valid</InputErrorMessage>
              )}
            </PendingEmailContainer>

            <SelectRoot
              value={invitation.role}
              onValueChange={value => model.updateField(idx, { role: value as OrganizationMemberRole })}
            >
              <RoleSelectTrigger data-testid={`invite-role-trigger-${idx}`}>
                <SelectValue placeholder="Role" />
                <Spring />
                <SelectIcon><ChevronDownLight className={Icon20} /></SelectIcon>
              </RoleSelectTrigger>

              <SelectPortal>
                <SelectContent data-testid="invite-role-dropdown">
                  <SelectViewport>
                    {["Admin", "Member"].map(type => (
                      <RoleSelectItem key={type} value={type} data-testid={`invite-role-option-${type.toLowerCase()}`}>
                        <SelectItemText>{type}</SelectItemText>
                      </RoleSelectItem>
                    ))}
                  </SelectViewport>
                </SelectContent>
              </SelectPortal>
            </SelectRoot>
          </PendingInvitationContainer>
        ))}
      </InvitationsContainer>

      <AddAnotherButton
        size="large"
        kind="alternative"
        onClick={() => model.addEmailField()}
        data-testid="invite-another"
      >
        + Add another
      </AddAnotherButton>

      {model.error.get() && (
        <FormErrorMessage>{formatErrorMessage(model.error.get(), "Unknown error")}</FormErrorMessage>
      )}

      <FormSubmitContainer>
        <Spring />

        <Button
          kind="alternative"
          size="large"
          onClick={() => setDialogOpen(false)}
          data-testid="invite-cancel"
        >
          Cancel
        </Button>

        <Button
          kind="primary"
          size="large"
          disabled={model.disabled.get()}
          onClick={async () => {
            const result = await model.submitInvitations()

            if (result) {
              setDialogOpen(false)
            }
          }}
          data-testid="invite-send"
        >
          {model.submitting.get()
            ? ellipsize("Sending")
            : invitationsToSend.length > 0
              ? `Send ${invitationsToSend.length} ${pluralize("invitation", invitationsToSend.length)}`
              : "Send"}
        </Button>
      </FormSubmitContainer>
    </InviteTeammatesFormContainer>
  )
})

export const UserSettingsTeammatesContent = observer(({ model }: {
  model: InviteTeammatesDialogModel
  setDialogOpen: (open: boolean) => void
}) => {
  const [inviteTeammatesDialogOpen, setInviteTeammatesDialogOpen] = useState(false)
  const [memberToRemove, setMemberToRemove] = useState<OrganizationMember | null>(null)

  return (
    <UserSettingsFormContainer>
      <MembersSection>
        {model.permissions.canInviteUsers.get() && (
          <>
            <InviteTeammatesButton
              onClick={() => setInviteTeammatesDialogOpen(true)}
              data-testid="settings-invite-teammates-button"
            >
              <InviteTeammatesButtonIcon />
              Invite a teammate
            </InviteTeammatesButton>

            <InviteTeammatesDialog
              dialogOpen={inviteTeammatesDialogOpen}
              setDialogOpen={setInviteTeammatesDialogOpen}
            />
          </>
        )}

        <MembersContainer hasDeletePermission={model.permissions.canDeleteUsers.get()}>
          {model.members.get().map((member, index) => {
            const { profile, role } = member

            const fullName = getFullName(profile)
            const emails = profile.emails?.join(", ") ?? ""

            return (
              <MemberRowWrapper key={`${profile.id}-${role}`} pendingDeletion={member.pendingDeletion}>
                <MemberContainer>
                  <Avatar profile={profile} size={36} />
                  <MemberDetailsContainer>
                    <MemberLink {...Navigator.user(model.context.orgId, profile.username)} viewTransition>
                      {fullName}
                    </MemberLink>
                    <Tooltip content={emails}>
                      <MemberEmail>{emails}</MemberEmail>
                    </Tooltip>
                  </MemberDetailsContainer>
                  <SelectRoot
                    value={role}
                    onValueChange={value => model.setOrganizationMemberRole(profile.id, value as OrganizationMemberRole, index)}
                    disabled={member.previousRole !== undefined || !model.canChangeRole(member) || !model.permissions.canChangeRole.get()}
                  >
                    <RoleSelectTrigger>
                      <SelectValue placeholder="Role" />
                      <Spring />
                      <SelectIcon><ChevronDownLight className={Icon20} /></SelectIcon>
                    </RoleSelectTrigger>

                    <SelectPortal>
                      <SelectContent>
                        <SelectViewport>
                          {["Admin", "Member"].map(type => (
                            <RoleSelectItem key={type} value={type}>
                              <SelectItemText>{type}</SelectItemText>
                            </RoleSelectItem>
                          ))}
                        </SelectViewport>
                      </SelectContent>
                    </SelectPortal>
                  </SelectRoot>

                  {model.permissions.canDeleteUsers.get() && (
                    <Tooltip content={model.isMe(member) ? undefined : "Remove teammate"}>
                      <DeleteButton
                        kind="icon"
                        disabled={model.isMe(member)}
                        onClick={() => setMemberToRemove(member)}
                      >
                        <DeleteLight />
                      </DeleteButton>
                    </Tooltip>
                  )}
                </MemberContainer>
              </MemberRowWrapper>
            )
          })}
        </MembersContainer>

        {model.isLoading.get() && <Spinner text={ellipsize("Loading")} />}

        <BatchLoader
          setBatch={() => void model.loadMembers()}
          batchSize={model.batchSize}
          nextOffset={model.nextOffset.get()}
          hasMore={model.hasMore.get()}
        />

        <ConfirmDialog
          open={memberToRemove != null}
          onOpenChange={(open) => {
            if (!open) setMemberToRemove(null)
          }}
          trigger={null}
          description={memberToRemove ? `Are you sure you want to remove ${getFullName(memberToRemove.profile)} from the organization?` : ""}
          confirmText="Remove"
          onConfirm={async () => {
            if (memberToRemove) {
              await model.removeOrganizationMember(memberToRemove.profile.id)
              setMemberToRemove(null)
            }
          }}
        />
      </MembersSection>
    </UserSettingsFormContainer>
  )
})

const InviteTeammatesFormContainer = styled(Vertical)`
  gap: 16px;
  align-self: stretch;
  margin-top: 16px;
`

const UserSettingsFormContainer = styled(Vertical)`
  gap: 16px;
  align-self: stretch;
`

const MembersSection = styled(Vertical)`
  gap: 16px;

  > ${InviteTeammatesButton} {
    align-self: flex-start;
  }
`

const FormSubmitContainer = styled(Horizontal)`
  align-items: center;
  gap: 8px;
`

const AddAnotherButton = styled(Button)`
  align-self: flex-start;
  margin-bottom: 16px;
`

const RoleSelectTrigger = styled(SelectTrigger)`
  width: 100%;
`

const RoleSelectItem = styled(SelectItem)`
  display: flex;
  align-items: center;
  height: ${INPUT_HEIGHT_PX}px;
`

const InputErrorMessage = styled(Horizontal)`
  align-items: center;

  position: absolute;
  right: ${INPUT_PADDING_PX}px;
  top: 0;

  height: ${INPUT_HEIGHT_PX}px;

  color: var(--error-color);

  ${textStyles.smallestFontSize};
`

const FormErrorMessage = styled.div`
  color: var(--error-color);
`

const MembersContainer = styled.div<{ hasDeletePermission: boolean }>`
  display: grid;
  grid-template-columns: ${props => props.hasDeletePermission ? "36px 1fr 1fr min-content 22px" : "36px 1fr 1fr min-content"};
  align-items: center;
  align-self: stretch;
  gap: 8px;

  @media (width <= 870px) {
    grid-template-columns: ${props => props.hasDeletePermission ? "36px 1fr min-content 22px" : "36px 1fr min-content"};
  }
`

const MemberRowWrapper = styled.div<{ pendingDeletion?: boolean }>`
  display: contents;

  ${props => props.pendingDeletion ? `
    opacity: 0.5;
    transition: opacity 0.3s ease-out;
    pointer-events: none;
  ` : ""}
`

const MemberContainer = styled.div`
  display: contents;
`

const MemberDetailsContainer = styled.div`
  display: contents;
`

const MemberLink = styled(Link)`
  color: var(--text-color);
  font-size: var(--base-font-size);
  font-weight: 500;
  line-height: 18px;

  ${ellipsis};

  min-width: 0;
`

const MemberEmail = styled(Ellipsis)`
  color: var(--text-color);
  font-size: var(--base-font-size);
  line-height: 18px;

  min-width: 0;
  margin-right: 8px;

  @media (width <= 870px) {
    display: none;
  }
`

const InvitationsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr min-content;
  align-self: stretch;
  gap: 16px;
`

const PendingInvitationContainer = styled.div`
  display: contents;
`

const PendingEmailContainer = styled.div`
  position: relative;
  min-width: 0;
`

const DeleteButton = styled(Button)`
  .svg-icon {
    color: var(--error-color-60);
  }
`

